import React, { useState, useEffect } from 'react';
import InputField, { FieldWrapper, getNumberValue } from './input_field';
import PropTypes from 'prop-types';
import createEnum from 'scripts/lib/create_enum';
import isNil from 'lodash/isNil';
import styled from 'styled-components';

/**
 * @visibleName Input With Time Convert Field
 */

export const TimeUnits = createEnum('NanoSeconds', 'Seconds', 'Minutes', 'Hours', 'StringFormat');

const MIN_IN_HOUR = 60;
const SEC_IN_MIN = 60;
const NANO_SEC_IN_SEC = 1000000000;

const timeStringConverter = (time = 0, from = TimeUnits.Seconds) => {
  const timeMap = [
    { key: TimeUnits.Hours, suffix: 'h', format: MIN_IN_HOUR * SEC_IN_MIN },
    { key: TimeUnits.Minutes, suffix: 'm', format: SEC_IN_MIN },
    { key: TimeUnits.Seconds, suffix: 's', format: 1 },
    { key: TimeUnits.NanoSeconds, suffix: 'ns', format: 1 / NANO_SEC_IN_SEC },
  ];
  const currentFlow = timeMap.find(({ key }) => key === from);
  const inSeconds = time * (currentFlow?.format || 1);
  const sec_num = parseInt(inSeconds, 10);
  const h = Math.floor(sec_num / 3600);
  const m = Math.floor(sec_num / 60) % 60;
  const s = sec_num % 60;
  return [h, m, s].map((v, i) => `${v}${timeMap[i].suffix}`).join('');
};

const timeStringParser = (time, to) => {
  const convertToUnit =
    [
      { key: TimeUnits.Seconds, format: 1 },
      { key: TimeUnits.Minutes, format: 1 / MIN_IN_HOUR },
      { key: TimeUnits.Hours, format: 1 / MIN_IN_HOUR / SEC_IN_MIN },
      { key: TimeUnits.NanoSeconds, format: 1 / MIN_IN_HOUR / SEC_IN_MIN / NANO_SEC_IN_SEC },
    ].find(({ key }) => key === to)?.format || 1;
  const units = { h: MIN_IN_HOUR * SEC_IN_MIN, m: SEC_IN_MIN, s: 1 };
  const regex = /(\d+)([hms])/g;
  let seconds = 0;
  let match;
  while ((match = regex.exec(time))) {
    seconds += parseInt(match[1]) * units[match[2]];
  }
  return seconds * convertToUnit;
};

const TimeUnitsMap = {
  [TimeUnits.Hours]: {
    [TimeUnits.Minutes]: value => MIN_IN_HOUR * value,
    [TimeUnits.Seconds]: value => value * MIN_IN_HOUR * SEC_IN_MIN,
    [TimeUnits.NanoSeconds]: value => value * MIN_IN_HOUR * SEC_IN_MIN * NANO_SEC_IN_SEC,
    [TimeUnits.StringFormat]: value => timeStringConverter(value, TimeUnits.Hours),
  },
  [TimeUnits.Minutes]: {
    [TimeUnits.Hours]: value => value / MIN_IN_HOUR,
    [TimeUnits.Seconds]: value => value * SEC_IN_MIN,
    [TimeUnits.NanoSeconds]: value => value * SEC_IN_MIN * NANO_SEC_IN_SEC,
    [TimeUnits.StringFormat]: value => timeStringConverter(value, TimeUnits.Minutes),
  },
  [TimeUnits.Seconds]: {
    [TimeUnits.Hours]: value => value / MIN_IN_HOUR / SEC_IN_MIN,
    [TimeUnits.Minutes]: value => value / SEC_IN_MIN,
    [TimeUnits.NanoSeconds]: value => value * NANO_SEC_IN_SEC,
    [TimeUnits.StringFormat]: value => timeStringConverter(value, TimeUnits.Seconds),
  },
  [TimeUnits.NanoSeconds]: {
    [TimeUnits.Hours]: value => value / MIN_IN_HOUR / SEC_IN_MIN / NANO_SEC_IN_SEC,
    [TimeUnits.Minutes]: value => value / SEC_IN_MIN / NANO_SEC_IN_SEC,
    [TimeUnits.Seconds]: value => value / NANO_SEC_IN_SEC,
    [TimeUnits.StringFormat]: value => timeStringConverter(value, TimeUnits.NanoSeconds),
  },
  [TimeUnits.StringFormat]: {
    [TimeUnits.Hours]: value => timeStringParser(value, TimeUnits.Hours),
    [TimeUnits.Minutes]: value => timeStringParser(value, TimeUnits.Minutes),
    [TimeUnits.Seconds]: value => timeStringParser(value, TimeUnits.Seconds),
    [TimeUnits.NanoSeconds]: value => timeStringParser(value, TimeUnits.NanoSeconds),
  },
};

export function convertTimeUnit(from, to, value, allowDecimalDigits = false) {
  if (isNil(value)) {
    return value;
  }
  const convertedValue = from === to ? value : TimeUnitsMap[from][to](value);
  if (to === TimeUnits.StringFormat) return convertedValue;
  return allowDecimalDigits ? Math.round(convertedValue * 100) / 100 : Math.round(convertedValue);
}

const InputWithTimeConvertField = React.forwardRef((props, ref) => {
  const {
    allowDecimalDigits,
    allowDecimalDigitsForSource,
    defaultValue,
    displayUnits,
    maxLength = 10,
    onChange,
    sourceUnits,
    suffix,
    suffixText,
    value,
    ...otherProperties
  } = props;

  const getDisplayValue = value => {
    const displayValue = convertTimeUnit(sourceUnits, displayUnits, value, allowDecimalDigits);
    return displayValue ? displayValue.toString() : defaultValue;
  };

  const [input, setInput] = useState({});

  useEffect(() => {
    if (value && input.value !== (value || 0)) {
      const displayValue = getDisplayValue(value);
      setInput({ displayValue, value });
    }
  }, [value, setInput, input]);

  const changeHandler = (val = '') => {
    let value = '';

    if (val) {
      if (!isPositiveNumber(val, allowDecimalDigits)) {
        return false;
      }
      value = parseFloat(val);
    }

    let valueInSourceUnits = convertTimeUnit(displayUnits, sourceUnits, value || 0, allowDecimalDigits);
    valueInSourceUnits =
      allowDecimalDigitsForSource || sourceUnits === TimeUnits.StringFormat
        ? valueInSourceUnits
        : Math.round(valueInSourceUnits);

    setInput({ value: valueInSourceUnits, displayValue: val });
    onChange && onChange(valueInSourceUnits);
  };

  return (
    <StyledInputField
      {...otherProperties}
      maxLength={maxLength}
      onChange={changeHandler}
      ref={ref}
      suffix={suffixText}
      value={input.displayValue}
    />
  );
});

function isPositiveNumber(strNumber, allowDecimalDigits) {
  if (allowDecimalDigits) {
    const [val, isValid] = getNumberValue(strNumber);
    return isValid && val >= 0;
  }

  const n = Math.floor(Number(strNumber));
  return n !== Infinity && String(n) === strNumber && n >= 0;
}

InputWithTimeConvertField.propTypes = {
  'data-aid': PropTypes.string,
  action: PropTypes.element,
  allowDecimalDigits: PropTypes.bool,
  allowDecimalDigitsForSource: PropTypes.bool,
  autoFocus: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  description: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  displayUnits: PropTypes.oneOf(['NanoSeconds', 'Seconds', 'Minutes', 'Hours', 'StringFormat']),
  error: PropTypes.string,
  inputType: PropTypes.oneOf(['number', 'text', 'password']),
  label: PropTypes.string,
  maxLength: PropTypes.number,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  sourceUnits: PropTypes.oneOf(['NanoSeconds', 'Seconds', 'Minutes', 'Hours', 'StringFormat']),
  success: PropTypes.string,
  suffixText: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const StyledInputField = styled(InputField)`
  ${FieldWrapper} {
    input {
      width: 100%;
    }

    &:after {
      content: '${p => p.suffix}';
      line-height: 1;
      margin-top: 1px;
      padding-right: 4px;
    }
  }
`;

export default InputWithTimeConvertField;
